/**
 * GENERATED FILE — DO NOT EDIT BY HAND.
 *
 * Regenerate with:  node scripts/gen-plan-pricing.cjs
 * Source of truth:  public.plan_definitions (product = 'mentionfox', published)
 * Verified by:      node scripts/check-one-price.cjs, which fails the build both
 *                   if this file drifts from the database and if any other file
 *                   under src/ or public/ names a plan price.
 *
 * THIS IS THE ONLY PLACE IN src/ THAT NAMES A MENTIONFOX PLAN PRICE. Four
 * separate ladders shipped in the dashboard at once before it existed — Pro at
 * $29, at $99, at $149, and a "Lite" tier that is not a tier — because
 * plan_definitions had the right columns and no rows, so every component grew
 * its own answer.
 *
 * NO ANNUAL PRICE IS PUBLISHED HERE. The /pricing page renders an annual toggle,
 * but no annual figure exists in plan_definitions, mentionfox_canonical_facts or
 * tier_credit_grants, so there is nothing to reconcile it against. It is left
 * out rather than copied from one surface into another.
 *
 * PACK PRICES ARE NOT HERE EITHER, and that is deliberate: the three records
 * that describe them disagree with each other. See the report of 2026-08-09.
 */

export type PlanSlug = "free" | "pro" | "agency" | "enterprise";

export interface PlanRecord {
  slug: PlanSlug;
  name: string;
  /** Formatted for display, e.g. "$99". Monthly. */
  monthlyPrice: string;
  /** Numeric monthly price in USD. */
  monthlyAmount: number;
  /** Monthly credit grant. Agrees with tier_credit_grants.monthly_credits. */
  monthlyCredits: number;
  /** Seat count, or "Unlimited". */
  seats: string;
}

/** Cheapest first. This is also the upgrade ladder. */
export const PLAN_LADDER: PlanSlug[] = ["free", "pro", "agency", "enterprise"];

export const PLANS: Record<PlanSlug, PlanRecord> = {
  free: { slug: "free", name: "Free", monthlyPrice: "$0", monthlyAmount: 0, monthlyCredits: 10, seats: "1" },
  pro: { slug: "pro", name: "Pro", monthlyPrice: "$99", monthlyAmount: 99, monthlyCredits: 100, seats: "1" },
  agency: { slug: "agency", name: "Agency", monthlyPrice: "$499", monthlyAmount: 499, monthlyCredits: 300, seats: "5" },
  enterprise: { slug: "enterprise", name: "Enterprise", monthlyPrice: "$2,999", monthlyAmount: 2999, monthlyCredits: 1000, seats: "Unlimited" },
};

/** free < pro < agency < enterprise. agency_plus is agency. */
export function tierRank(tier: string | null | undefined): number {
  const i = PLAN_LADDER.indexOf(planFor(tier).slug);
  return i < 0 ? 0 : i + 1;
}

export function tierAtLeast(tier: string | null | undefined, min: string): boolean {
  return tierRank(tier) >= tierRank(min);
}

/** Normalise a stored tier to a plan record. Unknown or absent reads as the first rung. */
export function planFor(tier: string | null | undefined): PlanRecord {
  const t = String(tier ?? "").toLowerCase();
  if (t === "agency_plus") return PLANS.agency;
  return (PLANS as Record<string, PlanRecord>)[t] ?? PLANS[PLAN_LADDER[0]];
}

/** The plans a user on `tier` could move up to, cheapest first. */
export function upgradesFrom(tier: string | null | undefined): PlanRecord[] {
  const here = PLAN_LADDER.indexOf(planFor(tier).slug);
  return PLAN_LADDER.slice(here + 1).map((s) => PLANS[s]);
}

/** Every plan, cheapest first — for a picker or a comparison table. */
export const ALL_PLANS: PlanRecord[] = PLAN_LADDER.map((s) => PLANS[s]);

/** Where a price belongs on a public page: a link, never a baked figure. */
export const PRICING_URL = "https://mentionfox.com/pricing";
